import React from 'react';
import { Box, Typography, Divider } from '@mui/material';

const ReportAppendices = ({ reportData }) => {
    return (
        <>
            <Divider sx={{ my: 2, mt: 4 }} />
            <Box className="report-section" sx={{ mb: 2, pageBreakBefore: 'always' }}>
                <Typography variant="h6" gutterBottom>Appendix A: Calculation Methodology</Typography>
                <Typography paragraph>
                    The financial projections in this report involve calculating the Gross Income (Future Value, FV) and, for future losses, the Present Value (PV) of anticipated income streams. These calculations divide each income source into monthly or partial-monthly segments. Segment-level results are aggregated annually for reporting.
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>Gross Income (Future Value – FV)</Typography>
                <Typography paragraph>
                    Gross Income for each segment is based on the input income amount, pay interval, and duration of the segment. A growth rate may also be applied (see note below).
                </Typography>
                <Typography component="div" sx={{ pl: 2, fontStyle: 'italic' }}>
                    <ol>
                        <li><strong>Monthly Equivalent:</strong> The income amount is first converted to a monthly equivalent based on its pay interval (e.g., annual / 12, weekly × ~4.345, etc.).</li>
                        <li><strong>Proration:</strong> This monthly equivalent is then prorated to reflect the number of days in the segment relative to the number of days in the month:
                            <br />
                            <code>GrossIncome<sub>segment</sub> = MonthlyEquivalentIncome × (DaysInSegment / DaysInMonthOfSegmentStart)</code>
                        </li>
                        <li><strong>Daily Pay Interval:</strong> For sources with a ‘Per Day’ pay interval, the calculation is direct:
                            <br />
                            <code>GrossIncome<sub>segment</sub> = DailyAmount × DaysInSegment</code>
                        </li>
                    </ol>
                </Typography>
                <Typography paragraph sx={{ mt: 1 }}>
                    The <strong>Net Loss</strong> for each segment is calculated as:
                    <br />
                    <code>NetLoss<sub>segment</sub> = GrossIncome<sub>segment</sub> × LostEarningCapacity</code>
                    <br />
                    The total Future Value (or Gross Income) for a year or income source is the sum of the relevant <code>GrossIncome<sub>segment</sub></code> or <code>NetLoss<sub>segment</sub></code> values across all applicable segments.
                </Typography>
                <Typography paragraph sx={{ mt: 1 }}>
                    <strong>Note on Growth Rate:</strong> If a growth rate is specified, it is applied as a compound increase to the base income amount over time. The annual growth rate is converted to a per-pay-period compound rate, which is applied from the income source's start date to the beginning of each calculation segment. This adjustment is applied <em>before</em> proration and other calculations to reflect projected income increases over time.
                </Typography>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Present Value (PV) of Future Loss</Typography>
                <Typography paragraph>
                    For loss periods after the <strong>Valuation Date</strong>, each segment’s <strong>Net Loss</strong> is discounted to determine its Present Value (PV). This represents the value of the anticipated income stream as of the valuation date.
                </Typography>
                <Typography component="div" sx={{ pl: 2, fontStyle: 'italic' }}>
                    <ol>
                        <li><strong>Discounting:</strong> Each segment is discounted using the following formula:
                            <br />
                            <code>PV<sub>segment</sub> = NetLoss<sub>segment</sub> / (1 + DiscountRate)<sup>t<sub>discount</sub></sup></code>
                            <br />
                            Where <code>t<sub>discount</sub></code> is the time in years from the <strong>end date of the segment</strong> to the Valuation Date, calculated as:
                            <br />
                            <code>t<sub>discount</sub> = DaysBetween / 365.25</code>
                        </li>
                    </ol>
                </Typography>
                <Typography paragraph sx={{ mt: 1 }}>
                    The total Present Value for a year or income source is the sum of its <code>PV<sub>segment</sub></code> values. This method assumes cash flow is received at the end of each segment.
                </Typography>
            </Box>
            <Divider sx={{ my: 2, mt: 4 }} />
            <Box className="report-section" sx={{ mb: 2, pageBreakBefore: 'always' }}>
                <Typography variant="h6" gutterBottom>Appendix B: Sources</Typography>
                {reportData.country === 'Canada' && (
                    <Box component="ul" sx={{ pl: 2, m: 0, listStylePosition: 'outside' }}>
                        <Typography component="li" variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                            <strong>Life Expectancy Data:</strong> Statistics Canada. Table 13-10-0114-01 Life expectancy and other elements of the complete life table, three-year estimates, Canada, all provinces except Prince Edward Island.
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                            <strong>Work Life Expectancy Data:</strong> Statistics Canada. (2024). Average and median retirement age by class of worker, annual (Table 14-10-0060-01). https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=1410006001
                        </Typography>
                    </Box>
                )}
                {reportData.country !== 'Canada' && (
                    <Box component="ul" sx={{ pl: 2, m: 0, listStylePosition: 'outside' }}>
                        <Typography component="li" variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                            <strong>Life Expectancy Data:</strong> U.S. State Life Tables, 2021. National Center for Health Statistics, National Vital Statistics System. NVSR 73(7).
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                            <strong>Work Life Expectancy Data:</strong> The Markov Process Model of Labor Force Activity: Extended Tables of Central Tendency, Shape, Percentile Points, and Bootstrap Standard Errors. (Tables for initially active with simplified average for non-binary sex).
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );
};

ReportAppendices.addPdfContent = (pdf, reportData, y) => {
    pdf.addPage();
    y = 15;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Appendix A: Calculation Methodology', 15, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const appendixA = [
        'The financial projections in this report involve calculating the Gross Income (Future Value, FV) and, for future losses, the Present Value (PV) of anticipated income streams.',
        'These calculations divide each income source into monthly or partial-monthly segments. Segment-level results are aggregated annually for reporting.',
        '',
        'Gross Income (Future Value – FV):',
        'Gross Income for each segment is based on the input income amount, pay interval, and duration of the segment. A growth rate may also be applied.',
        'Net Loss for each segment is calculated as: NetLoss_segment = GrossIncome_segment × LostEarningCapacity',
        'The total Future Value (or Gross Income) for a year or income source is the sum of the relevant segment values.',
        '',
        'Present Value (PV) of Future Loss:',
        'For loss periods after the Valuation Date, each segment’s Net Loss is discounted to determine its Present Value (PV).',
        'PV_segment = NetLoss_segment / (1 + DiscountRate)^t_discount, where t_discount is the time in years from the end date of the segment to the Valuation Date.',
        'The total Present Value for a year or income source is the sum of its segment PVs.',
    ];
    appendixA.forEach(line => {
        const wrapped = pdf.splitTextToSize(line, 180);
        pdf.text(wrapped, 15, y);
        y += 6 * wrapped.length;
    });
    y += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Appendix B: Sources', 15, y);
    y += 8;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    let sources = [];
    if (reportData.country === 'Canada') {
        sources = [
            'Life Expectancy Data: Statistics Canada. Table 13-10-0114-01 Life expectancy and other elements of the complete life table, three-year estimates, Canada, all provinces except Prince Edward Island.',
            'Work Life Expectancy Data: Statistics Canada. (2024). Average and median retirement age by class of worker, annual (Table 14-10-0060-01). https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=1410006001'
        ];
    } else {
        sources = [
            'Life Expectancy Data: U.S. State Life Tables, 2021. National Center for Health Statistics, National Vital Statistics System. NVSR 73(7).',
            'Work Life Expectancy Data: The Markov Process Model of Labor Force Activity: Extended Tables of Central Tendency, Shape, Percentile Points, and Bootstrap Standard Errors. (Tables for initially active with simplified average for non-binary sex).'
        ];
    }
    sources.forEach(source => {
        const wrapped = pdf.splitTextToSize(source, 170);
        pdf.text(['• ' + wrapped[0], ...wrapped.slice(1)], 18, y);
        y += 7 * wrapped.length;
    });

    return y;
};

export default ReportAppendices;